import React, { FC, useRef } from 'react';
import {
    Box,
    ButtonBase,
    ClickAwayListener,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { AccountCircle, KeyboardArrowDown } from '@mui/icons-material';
import { IAccountMenu } from './types';
import { useHistory } from 'react-router';
import { useBrandVersion } from '../../hooks';
import { portalUserService } from '../../services';

interface IAccountDropdown {
    accountMenu: IAccountMenu;
    /**
     * When true, render with dark text/icons suitable for a light (white) background,
     * such as the white top header used by PortalNavShellV2. Defaults to false (white
     * text), which matches the legacy colored TopBarHeaderMenu background.
     */
    onLightBackground?: boolean;
}

export const AccountDropdown: FC<IAccountDropdown> = ({ accountMenu, onLightBackground = false }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLDivElement>(null);
    const accountRef = useRef<HTMLDivElement>(null);
    const isMenuOpen = Boolean(anchorEl);
    const history = useHistory();
    const brandVersion = useBrandVersion();

    const isNewBrand = brandVersion === 'newBrand';
    const company = portalUserService.getCurrentCompany();
    const companyName = company?.ShortName || company?.Name;
    const fullCompanyName = company?.Name || companyName;

    const handleProfileMenuOpen = () => {
        setAnchorEl(accountRef.current);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                sx={{ color: onLightBackground ? 'text.primary' : 'white' }}
                data-testid="menu-account-icon"
            >
                {isNewBrand && companyName && (
                    <Tooltip
                        title={fullCompanyName ?? ''}
                        arrow
                        disableFocusListener
                        disableTouchListener
                        componentsProps={{ tooltip: { sx: { fontSize: '14px' } } }}
                    >
                        <Typography
                            variant="inherit"
                            noWrap
                            onMouseDown={(event) => event.preventDefault()}
                            sx={{
                                pl: 2.5,
                                maxWidth: { lg: 180, xl: 250 },
                                display: { xs: 'none', lg: 'block' },
                                userSelect: 'none',
                            }}
                        >
                            {companyName}
                        </Typography>
                    </Tooltip>
                )}
                <Box
                    ref={accountRef}
                    sx={{
                        bgcolor: isNewBrand ? 'transparent' : 'primary.800',
                        height: '100%',
                        p: 2.5,
                    }}
                >
                    <ButtonBase
                        aria-label="account of current user"
                        aria-controls={isMenuOpen ? 'user-settings-menu' : undefined}
                        aria-haspopup="true"
                        onClick={handleProfileMenuOpen}
                        color="inherit"
                        sx={{ color: 'inherit' }}
                    >
                        <AccountCircle sx={{ fontSize: 32, mr: 2 }} />
                        <Typography variant="inherit" noWrap>
                            {accountMenu.username}
                        </Typography>
                        <KeyboardArrowDown sx={{ ml: 2 }} />
                    </ButtonBase>
                </Box>
            </Stack>
            <Popper open={isMenuOpen} anchorEl={accountRef.current} disablePortal sx={{ zIndex: 9999 }}>
                <Paper>
                    <ClickAwayListener onClickAway={handleMenuClose}>
                        <MenuList id="user-settings-menu">
                            {accountMenu.items.map((accountItem, index) => (
                                <MenuItem
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    onClick={() => {
                                        if (accountItem.action) {
                                            accountItem.action();
                                        } else {
                                            history.push(accountItem.path);
                                        }
                                        handleMenuClose();
                                    }}
                                    data-testid={`menu-account-option-${accountItem.testId ?? accountItem.name}`}
                                >
                                    <Typography variant="inherit" noWrap>
                                        {accountItem.name}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </MenuList>
                    </ClickAwayListener>
                </Paper>
            </Popper>
        </>
    );
};
